import clsx from 'clsx';
import React, { useEffect } from 'react';

import Button from '@components/Button';
import { PrimaryButton, ProButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import {
  finance_path,
  subscribe_path,
  technology_path,
  upgrade_subscription_path,
  weekend_path,
} from '@routes';
import { ClickTarget, click } from '@tracking';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import DefaultSubmenuNavItem from '../SubmenuNav/DefaultSubmenuNav/NavItem';
import NavItemList from '../TopNav/NavItemList';
import {
  COMMUNITY_SUBMENU_NAV_ITEMS,
  MOBILE_SIDE_NAV_ITEMS,
  MORE_SUBMENU_MAIN_NAV_ITEMS,
  MORE_SUBMENU_SOCIAL_NAV_ITEMS,
  MORE_SUBNAV_FOOTER_NAV_ITEMS,
  PRO_SPECIAL_SUBMENU_NAV_ITEMS,
  PRO_SUBMENU_NAV_ITEMS,
} from '../navItems';
import ExpandableMenu from './ExpandableMenu';
import MenuLink from './MenuLink';

interface SideBarProps {
  currentUser: NavCurrentUser;
  isSideNavOpen: boolean;
  setIsSideNavOpen: (isSideNavOpen: boolean) => void;
}

const SideNav = ({ currentUser, isSideNavOpen }: SideBarProps) => {
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    const topBanners = document.querySelectorAll('.top-sticky-banner');

    if (isSideNavOpen) {
      document.body.style.overflow = 'hidden';
      if (topBanners) {
        // Hide the top banner when the side nav is open to prevent the design from breaking
        topBanners.forEach((banner: HTMLElement) => (banner.style.display = 'none'));
      }
    } else {
      document.body.style.overflow = 'unset';
      if (topBanners) {
        topBanners.forEach((banner: HTMLElement) => (banner.style.display = 'block'));
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSideNavOpen]);

  return (
    <div
      className={clsx(
        'fixed left-0 right-0 top-56 z-[10000] h-[calc(100vh-56px)] overflow-y-scroll bg-grayscale-charcoal duration-300 ease-in sm:hidden',
        {
          '-translate-x-full': !isSideNavOpen,
          'translate-x-0': isSideNavOpen,
        }
      )}
      tabIndex={-1}
    >
      <nav className="flex flex-col gap-24 border-0 border-b border-solid border-grayscale-beige px-36 pb-[30px] pt-24">
        {!currentUser.isSubscriber && (
          <PrimaryButton
            as="link"
            href={subscribe_path()}
            className="w-full"
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
          >
            Subscribe
          </PrimaryButton>
        )}
        {currentUser.isSubscriber &&
          !currentUser.isProSubscriber &&
          !currentUser.willSubscriptionChange && (
            <ProButton
              as="link"
              className="w-full"
              href={upgrade_subscription_path()}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
            >
              Upgrade to Pro
            </ProButton>
          )}
        <ExpandableMenu
          name="Pro"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'Pro' }))
          }
          isPro
        >
          <NavItemList className="space-y-10">
            {PRO_SUBMENU_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
          <h2 className="mt-32 font-suisse-intl-mono text-overline-md font-bold text-pro-gold">
            Special Projects
          </h2>
          <NavItemList className="space-y-10">
            {PRO_SPECIAL_SUBMENU_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
        <MenuLink
          name="Tech"
          as="link"
          href={technology_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Tech }))}
        />
        <MenuLink
          name="Finance"
          as="link"
          href={finance_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Finance }))}
        />
        <MenuLink
          name="Weekend"
          as="link"
          href={weekend_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Weekend }))}
        />
        <ExpandableMenu
          name="Community"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'Community' }))
          }
        >
          <NavItemList className="space-y-24">
            {COMMUNITY_SUBMENU_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.CommunityMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
        <ExpandableMenu
          name="More"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'More' }))
          }
        >
          <NavItemList className="space-y-24">
            {MORE_SUBMENU_MAIN_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.MoreMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
      </nav>
      <nav className="h-screen min-h-webkit-fill-available px-72 pb-48 pt-[30px]">
        <NavItemList className="space-y-32">
          {MOBILE_SIDE_NAV_ITEMS.map(({ name, ...rest }) => (
            <DefaultSubmenuNavItem
              as="link"
              key={name}
              className="font-suisse-screen text-overline-lg"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.SideNavItem, clickText: name }))
              }
              {...rest}
            >
              {name}
            </DefaultSubmenuNavItem>
          ))}
        </NavItemList>
        <NavItemList className="mt-40 flex gap-[18px]">
          {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
            <Button
              key={name}
              as="externalLink"
              className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-solid border-white bg-transparent text-white transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.SocialNavItem, clickText: name }))
              }
              {...rest}
            >
              <Icon className="h-12" />
              <span className="sr-only">{name}</span>
            </Button>
          ))}
        </NavItemList>
        <NavItemList className="mt-32 flex gap-24">
          {MORE_SUBNAV_FOOTER_NAV_ITEMS.map(({ name, hasExternalLink, ...rest }) => (
            <li key={name}>
              <Button
                as={hasExternalLink ? 'externalLink' : 'link'}
                className="font-suisse-screen text-disclosure-md leading-[1.3] text-white opacity-50 transition-all hover:text-primary-magenta hover:opacity-100 focus:opacity-100"
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.FinePrintNavItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </Button>
            </li>
          ))}
        </NavItemList>
      </nav>
    </div>
  );
};

export default SideNav;
